import { BmHeaderPeriodForm, BmItemForm, BudgetModelFormGroup } from "../../pojo/bm-form";
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { BmCalcHelperService } from "./bm-calc-helper.service";
import { BmExpensesType, BmPeriodEnum, BmTotalType } from "../../pojo/bm-classes";
import { BmCalcType } from "../../pojo/bm-enums";
import { BmGrandTotalMgmFeeCalcService } from "../mgmFee/total/bm-grand-total-mgm-fee-calc.service";
import { BmExpensesCalcService } from "../expenses/bm-expenses-calc.service";
import { BmInvestmentFormService } from "../investment/bm-investment-form.service";
import { BmTotalAccumulatedCapCalcService } from "../total/accumulated/bm-total-accumulated-cap-calc.service";
import { MgmFeeValuesFormService } from "../mgmFee/mgmFee/mgm-fee-values-form.service";
import { BmGroupTotalCalculationsService } from "../mgmFee/total/bm-group-total-calculations.service";
import { CatchUpService } from "../mgmFee/catchUp/catch-up.service";
import { BmMgmVatFormService } from "../mgmFee/vat/bm-mgm-vat-form.service";
import { BmTotalGeneralCalcService } from "../total/general/BmTotalGeneralCalcService";
import { DATA_FILLING_TYPE_CONVERTERS, DEFAULT_ITEMS_NUMBER_IN_YEAR } from "../../pojo/bm-const";


export class BmYearQuarterService {

  public static createModelHeaders(yearNumber: number): FormArray<FormGroup<BmHeaderPeriodForm>> {
    const headers = new FormArray<FormGroup<BmHeaderPeriodForm>>([]);

    for (let y = 1; y <= yearNumber; y++) {
      for (let q = 1; q <= 4; q++) {
        headers.push(new FormGroup<BmHeaderPeriodForm>({
          headerText: new FormControl<string>(`Q${q}`),
          type: new FormControl<BmPeriodEnum>(BmPeriodEnum.QUARTER),
          quarterNumber: new FormControl<number>(q),
          yearNumber: new FormControl<number>(y),
          isCollapsed: new FormControl(false),
        }));
      }
      headers.push(new FormGroup<BmHeaderPeriodForm>({
        headerText: new FormControl<string>(`Y${y}`),
        type: new FormControl<BmPeriodEnum>(BmPeriodEnum.YEAR),
        quarterNumber: new FormControl<number>(null),
        yearNumber: new FormControl<number>(y),
        isCollapsed: new FormControl(false),
      }));
    }
    return headers;
  }

  public static updateModelHeaders(yearNumber: number, budgetForm: BudgetModelFormGroup): void {
    const hdrFA = budgetForm.controls.modelHeaders;
    const required = yearNumber * DEFAULT_ITEMS_NUMBER_IN_YEAR;
    const current = hdrFA.length;

    if (current > required) {
      for (let i = current - 1; i >= required; i--) {
        hdrFA.removeAt(i);
      }
    }

    if (current < required) {
      const startY = Math.floor(current / 5) + 1; // с какого года начинаем
      for (let y = startY; y <= yearNumber; y++) {
        for (let q = 1; q <= 4; q++) {
          hdrFA.push(new FormGroup<BmHeaderPeriodForm>({
            headerText: new FormControl(`Q${q}`),
            type: new FormControl(BmPeriodEnum.QUARTER),
            quarterNumber: new FormControl(q),
            yearNumber: new FormControl(y),
            isCollapsed: new FormControl(false),
          }));
        }
        hdrFA.push(new FormGroup<BmHeaderPeriodForm>({
          headerText: new FormControl(`Y${y}`),
          type: new FormControl(BmPeriodEnum.YEAR),
          quarterNumber: new FormControl(0),
          yearNumber: new FormControl(y),
          isCollapsed: new FormControl(false),
        }));
      }
    }
  }

  public static applyYearDelta(budgetForm: BudgetModelFormGroup): void {
    const newY = Number(budgetForm.controls.yearsNumber.getRawValue() || 0);
    const oldY = Number(budgetForm.controls.previousYearsNumber.getRawValue() || 0);
    if (newY === oldY) {
      return;
    }

    this.updateMgmFee(budgetForm, oldY, newY);
    this.updateExpenses(budgetForm, oldY, newY, "operatingExpenses");
    this.updateExpenses(budgetForm, oldY, newY, "organizationalExpenses");
    this.updateTotalCapSection(budgetForm, oldY, newY, "totalExpenses");
    this.updateInvestment(budgetForm, oldY, newY);
    this.updateTotalCapSection(budgetForm, oldY, newY, "totalCapRequired");
    this.updateTotalCapSection(budgetForm, oldY, newY, "totalCapAccumulated");

    budgetForm.controls.previousYearsNumber.patchValue(newY);

    budgetForm.controls.modelData.controls.mgmFee.controls.groups.controls.forEach((mgmFeeFormGroup) => {
      MgmFeeValuesFormService.mgmFeeValuesRecalculate(mgmFeeFormGroup);
      CatchUpService.catchUpValuesRecalculate(mgmFeeFormGroup);
      BmMgmVatFormService.recalculateVatValues(mgmFeeFormGroup);
      BmGroupTotalCalculationsService.groupTotalRecalculate(mgmFeeFormGroup);
    });

    BmGrandTotalMgmFeeCalcService.recalculateGrandTotal(budgetForm.controls.modelData.controls.mgmFee);
    BmExpensesCalcService.recalculateSectionTotal(budgetForm.controls.modelData.controls.operatingExpenses);
    BmExpensesCalcService.recalculateSectionTotal(budgetForm.controls.modelData.controls.organizationalExpenses);
    BmInvestmentFormService.recalculateInvestment(budgetForm.controls.modelData.controls.investment);

    BmTotalGeneralCalcService.recalculateTotalExpenses(budgetForm, budgetForm.controls.modelData.controls.totalCapRequired, true);
    BmTotalGeneralCalcService.recalculateTotalExpenses(budgetForm, budgetForm.controls.modelData.controls.totalExpenses, false);
    BmTotalAccumulatedCapCalcService.recalculateTotalAccumulatedRequired(budgetForm);
  }

  private static updateMgmFee(form: BudgetModelFormGroup, oldYears: number, newYears: number): void {
    const groupsFA = form.controls.modelData.controls.mgmFee.controls.groups;
    // for each subgroup in each group…
    groupsFA.controls.forEach((grp) => {
      const subs = grp.controls.subGroups.controls;
      for (const key of Object.keys(subs)) {
        this.resizeTimeline(
          (subs[key] as FormGroup).controls.items as FormArray<FormGroup<BmItemForm>>,
          (subs[key] as FormGroup).controls.standardValue?.value ?? 0,
          oldYears,
          newYears,
          key === "mgmFeesPcnt"
        );
      }
      // each group's own total array
      this.resizeTimeline(
        grp.controls.total,
        0,
        oldYears,
        newYears
      );
    });

    // grand-total
    this.resizeTimeline(form.controls.modelData.controls.mgmFee.controls.total, 0, oldYears, newYears
    );
  }

  private static updateExpenses(form: BudgetModelFormGroup, oldYears: number, newYears: number, section: BmExpensesType): void {
    const root = form.controls.modelData.controls[section];
    root.controls.groups.controls.forEach((grp) => {
      const arr = grp.controls.items;
      const std = grp.controls.standardValue.value;
      // choose the right qFn/yFn from your DataFillingType
      const type = grp.controls.dataFillingType.value;
      const [qFn, yFn] = DATA_FILLING_TYPE_CONVERTERS[type];
      this.resizeTimelineWithFns(arr, std, oldYears, newYears, qFn, yFn);
    });
    // section grand-total
    this.resizeTimeline(root.controls.total, 0, oldYears, newYears);
  }

  private static updateInvestment(
    form: BudgetModelFormGroup,
    oldYears: number,
    newYears: number
  ): void {
    const inv = form.controls.modelData.controls.investment;
    // items
    this.resizeTimeline(
      inv.controls.itemsPcnt,
      0,
      oldYears,
      newYears
    );
    // total
    this.resizeTimeline(
      inv.controls.itemsValue,
      0,
      oldYears,
      newYears
    );
  }

  /**
   * Generic grow/shrink, using the standard quarter=base, year=base fn.
   */
  private static resizeTimeline(
    arr: FormArray<FormGroup<BmItemForm>>,
    base: number,
    oldYears: number,
    newYears: number,
    quarterDivide?: boolean
  ): void {
    const qFn = quarterDivide ? (v: number): number => v / 4 : ((v: number): number => v);
    const yFn = (v: number): number => v;
    this.resizeTimelineWithFns(arr, base, oldYears, newYears, qFn, yFn);
  }

  /**
   * Generic grow/shrink for any two functions.
   */
  private static resizeTimelineWithFns(
    arr: FormArray<FormGroup<BmItemForm>>,
    base: number,
    oldYears: number,
    newYears: number,
    quarterFn: (v: number) => number,
    yearFn: (v: number) => number
  ): void {
    const oldY = Math.floor(oldYears);
    if (newYears > oldYears) {
      for (let y = oldY + 1; y <= newYears; y++) {
        for (let q = 1; q <= 4; q++) {
          const fg = BmCalcHelperService.createBmItemForm({
            uuid: null,
            period: { type: BmPeriodEnum.QUARTER, yearNumber: y, quarterNumber: q },
            itemValue: quarterFn(base),
            calcType: BmCalcType.GENERATED,
          });
          fg.controls.itemValue.disable({ emitEvent: false });
          arr.push(fg, { emitEvent: false });
        }
        const yfg = BmCalcHelperService.createBmItemForm({
          uuid: null,
          period: { type: BmPeriodEnum.YEAR, yearNumber: y, quarterNumber: null },
          itemValue: yearFn(base),
          calcType: BmCalcType.GENERATED,
        });
        yfg.controls.itemValue.disable({ emitEvent: false });
        arr.push(yfg, { emitEvent: false });
      }
    } else {
      for (let i = arr.length - 1; i >= 0; i--) {
        const p = arr.at(i).controls.period.value;
        if (p.yearNumber > newYears) {
          arr.removeAt(i, { emitEvent: false });
        }
      }
    }
  }

  private static updateTotalCapSection(form: BudgetModelFormGroup, oldYears: number, newYears: number, sectionKey: BmTotalType): void {
    const sec = form.controls.modelData.controls[sectionKey];

    // values[]
    this.resizeTimeline(
      sec.controls.values,
      0,
      oldYears,
      newYears
    );
    // pct[]
    this.resizeTimeline(
      sec.controls.pcnt,
      0,
      oldYears,
      newYears
    );
  }
}
